import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { useChatStore } from "@stores/chat.store";
import { useAdvertiserStore } from "@stores/advertiser";
import { CHAT_USER_TAG, USER_TAG } from "@/utils/constants";
import { debounce } from "@/utils/helpers";
import { useConversationMessages } from "../../hooks/conversation-messages";
import { useChatMessages } from "@hooks/api/chat-websocket";
import { useSensitiveDataWarning } from "../../hooks/sensitive-data-warning-state";

import type { TConversationAreaHookResult, TScrollerElement } from "./conversation-area.types";

const useForceScrollDown = (
  ref: React.RefObject<HTMLDivElement | null>,
  shouldUpdate: boolean,
  onScrolled: () => void,
): void => {
  useLayoutEffect(() => {
    if (ref.current?.scrollHeight && shouldUpdate) {
      ref.current.scrollTop = ref.current.scrollHeight;
      onScrolled();
    }
  }, [ref, shouldUpdate, onScrolled]);
};

/** Gerencia scroll automático, carregamento de mensagens antigas e lógica interna do painel de conversa. */
export const useConversationArea = (): TConversationAreaHookResult => {
  const advertiserSource = useAdvertiserStore((s) => s.source);
  const myId = Number(advertiserSource?.Retorno?.Id) || 0;
  const { fetchOlderMessageBatch, loadOlderMessages } = useChatMessages();

  const messageListChatId = useChatStore((s) => s.messageList.chatId);
  const messageListPagination = useChatStore((s) => s.messageList.pagination);
  const {
    hasOlderMessages,
    hasOlderMessagesToFetch,
    loadedOlderMessages,
    fetchingOlderMessages,
  } = messageListPagination;

  const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);
  const conversationActiveUserTag = useChatStore(
    (s) => s.conversation.active?.userTag,
  );

	const scrollerRef = useRef<TScrollerElement>(null);

  const { messages, newMessage } = useConversationMessages(myId);

  const userTag = conversationActiveUserTag
    ? (CHAT_USER_TAG[conversationActiveUserTag as keyof typeof CHAT_USER_TAG] ?? false)
    : false;

  const isBlocked = conversationActiveUserTag === USER_TAG.Blocked;
  const isSellingTab = storeSelectedTabIndex === 1;

  const [updateScroll, setUpdateScroll] = useState(true);
  const clearUpdateScroll = useCallback(() => setUpdateScroll(false), []);

  const forceScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollDownElement = useCallback((node: HTMLDivElement | null) => {
    forceScrollRef.current = node;
  }, []);

  useForceScrollDown(forceScrollRef, updateScroll, clearUpdateScroll);

  useLayoutEffect(() => {
    if (isBlocked || !scrollerRef.current) return;
    if (!fetchingOlderMessages) {
      const { scrollHeight, scrollTop } = scrollerRef.current;
      scrollerRef.current.lastHeight = scrollHeight - scrollTop;
    }
  }, [fetchingOlderMessages, isBlocked]);

  useLayoutEffect(() => {
    if (isBlocked || !scrollerRef.current) return;
    const scrollerNode = scrollerRef.current;
    const loadOrFetchMessages = hasOlderMessages || hasOlderMessagesToFetch;

    const getOlderMessages = debounce((height: number) => {
      if (!scrollerRef.current) return;
      scrollerRef.current.lastHeight = height;
      const notFetchingAndHasOlderMessages =
        !fetchingOlderMessages &&
        hasOlderMessagesToFetch &&
        typeof hasOlderMessagesToFetch !== "boolean";
      if (hasOlderMessages) loadOlderMessages();
      return (
        notFetchingAndHasOlderMessages &&
        messageListChatId &&
        fetchOlderMessageBatch(messageListChatId, hasOlderMessagesToFetch)
      );
    }, 2000);

    const handleScroll = ({ target }: Event) => {
      const { scrollHeight, scrollTop } = target as HTMLElement;
      const passedTheTreshold = scrollTop === 0;
      if (passedTheTreshold && loadOrFetchMessages) {
        getOlderMessages(scrollHeight);
      }
    };
    scrollerNode.addEventListener("scroll", handleScroll);
    return () => scrollerNode.removeEventListener("scroll", handleScroll);
  }, [
    hasOlderMessages,
    hasOlderMessagesToFetch,
    fetchingOlderMessages,
    isBlocked,
    messageListChatId,
    fetchOlderMessageBatch,
    loadOlderMessages,
  ]);

  useLayoutEffect(() => {
    if (isBlocked || !scrollerRef.current) return;
    if (loadedOlderMessages && messages && newMessage === false) {
      const { scrollHeight, lastHeight } = scrollerRef.current;
      scrollerRef.current.scrollTop = scrollHeight - lastHeight;
    } else if (messages && newMessage) {
      const timer = setTimeout(() => setUpdateScroll(true), 0);
      return () => clearTimeout(timer);
    }
  }, [loadedOlderMessages, messages, newMessage, isBlocked]);

  useLayoutEffect(() => {
    if (isBlocked) return;
    setTimeout(() => setUpdateScroll(true), 100);
  }, [isBlocked]);

  const isRenderSender =
    !storeSelectedTabIndex ||
    (userTag !== CHAT_USER_TAG.Spam && !!storeSelectedTabIndex);

  const { sensitiveDataWarning, toggleSensitiveDataWarning } =
    useSensitiveDataWarning();

  return {
    messages,
    isBlocked,
    isSellingTab,
    isRenderSender,
    scrollerRef,
    scrollDownElement,
    sensitiveDataWarning,
    toggleSensitiveDataWarning,
  };
};
